import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, MenuItem, Button, Card, CardContent } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '../../api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#ffc658', '#e91e63', '#9c27b0'];

const EmployeeDashboard = () => {
  const currentYear = new Date().getFullYear();
  
  const [filters, setFilters] = useState({ 
    start_date: `${currentYear}-01-01`, 
    end_date: `${currentYear}-12-31`, 
    department_id: '', 
    designation_id: '',
    status: '' 
  });
  
  const [dropdowns, setDropdowns] = useState({ departments: [], designations: [] });
  const [stats, setStats] = useState({ cards: {}, trend_graph: [], dept_active: [], dept_resigned: [], dept_inactive: [] });

  useEffect(() => {
    api.get('/correction-dashboard/filters-data').then(res => setDropdowns(res.data)).catch(console.error);
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    const query = new URLSearchParams(Object.entries(filters).filter(([_, v]) => v !== '')).toString();
    try {
      const res = await api.get(`/employee-exit/dashboard-stats?${query}`);
      setStats(res.data);
    } catch (err) { console.error("Failed to fetch employee stats", err); }
  };

  const getDisplayDateText = () => {
    if (filters.start_date && filters.end_date) {
      return `${filters.start_date} to ${filters.end_date}`;
    } else if (filters.start_date) {
      return `From ${filters.start_date}`;
    } else if (filters.end_date) {
      return `Up to ${filters.end_date}`;
    }
    return 'All Time Data';
  };

  return (
    <Box sx={{ p: 1, mt: 1 }}>
      {/* ROW 1: FILTERS */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderRadius: 2, border: '1px solid #eee' }} elevation={0}>
        <TextField size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={filters.start_date} onChange={e => setFilters({...filters, start_date: e.target.value})} sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
        <TextField size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }} value={filters.end_date} onChange={e => setFilters({...filters, end_date: e.target.value})} sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
        
        <TextField select size="small" label="Department" sx={{ minWidth: 140, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} value={filters.department_id} onChange={e => setFilters({...filters, department_id: e.target.value})}>
          <MenuItem value="" sx={{ fontSize: '0.85rem' }}>All Departments</MenuItem>
          {dropdowns.departments?.map(d => <MenuItem key={d.id} value={d.id} sx={{ fontSize: '0.85rem' }}>{d.department_name}</MenuItem>)}
        </TextField>

        <TextField select size="small" label="Designation" sx={{ minWidth: 140, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} value={filters.designation_id} onChange={e => setFilters({...filters, designation_id: e.target.value})}>
          <MenuItem value="" sx={{ fontSize: '0.85rem' }}>All Designations</MenuItem>
          {dropdowns.designations?.map(d => <MenuItem key={d.id} value={d.id} sx={{ fontSize: '0.85rem' }}>{d.designation_name}</MenuItem>)}
        </TextField>

        <TextField select size="small" label="Status" sx={{ minWidth: 120, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})}>
          <MenuItem value="" sx={{ fontSize: '0.85rem' }}>All Status</MenuItem>
          <MenuItem value="ACTIVE" sx={{ fontSize: '0.85rem' }}>Active</MenuItem>
          <MenuItem value="INACTIVE" sx={{ fontSize: '0.85rem' }}>Inactive</MenuItem>
          <MenuItem value="RESIGNED" sx={{ fontSize: '0.85rem' }}>Resigned</MenuItem>
          <MenuItem value="NOTICE" sx={{ fontSize: '0.85rem' }}>Notice</MenuItem>
        </TextField>

        <Button variant="contained" size="small" onClick={fetchStats} disableElevation sx={{ py: 1, bgcolor: '#1976d2' }}>Apply Filters</Button>
      </Paper>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2, pl: 0.5, fontWeight: 500 }}>
        Showing Employee Trends for: <strong style={{ color: '#10394f' }}>{getDisplayDateText()}</strong>
      </Typography>

      {/* ROW 2: COUNT CARDS */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1, mb: 1 }}>
        {[
          { label: 'Total Employees', value: stats.cards.total_employees || 0, color: '#1976d2' },
          { label: 'Active Employees', value: stats.cards.active_count || 0, color: '#2e7d32' },
          { label: 'Resigned', value: stats.cards.resigned_count || 0, color: '#ed6c02' },
          { label: 'Inactive / Exited', value: stats.cards.inactive_count || 0, color: '#d32f2f' },
          { label: 'On Notice', value: stats.cards.notice_count || 0, color: '#9c27b0', note: '(Date filter not applicable)' }
        ].map((card, idx) => (
          <Box key={idx} sx={{ width: { xs: '100%', sm: '50%', md: '20%' }, p: 1 }}>
            <Card sx={{ borderTop: `4px solid ${card.color}`, boxShadow: '0 2px 10px rgba(0,0,0,0.03)', borderRadius: 2 }} elevation={0}>
              <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: card.color }}>{card.value}</Typography>
                <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, display: 'block' }}>{card.label}</Typography>
                {card.note && <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#999', display: 'block', mt: 0.5 }}>{card.note}</Typography>}
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* ROW 3: TREND GRAPH */}
      <Box sx={{ mx: -1, mb: 1, p: 1 }}>
        <Paper sx={{ p: 2, height: 350, display: 'flex', flexDirection: 'column', borderRadius: 2, border: '1px solid #eee' }} elevation={0}>
          <Typography variant="body1" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#444' }}>Employment Trends (New Joiners vs Exits)</Typography>
          <Box sx={{ flexGrow: 1, minHeight: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.trend_graph} margin={{ left: -10, right: 15, top: 20, bottom: 0 }}>
                <XAxis dataKey="date_str" fontSize={11} tick={{ fill: '#777' }} />
                <YAxis allowDecimals={false} fontSize={11} tick={{ fill: '#777' }} />
                <Tooltip contentStyle={{ fontSize: '12px', borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                <Line type="monotone" name="New Joiners (DOJ)" dataKey="active_doj" stroke="#4caf50" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" name="Resignations (DOE)" dataKey="resigned_doe" stroke="#ff9800" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" name="Inactive Exits (DOE)" dataKey="inactive_doe" stroke="#f44336" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>

      {/* ROW 4: PIE CHARTS */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1, mb: 1 }}>
        <Box sx={{ width: { xs: '100%', md: '33.33%' }, p: 1 }}>
          <Paper sx={{ p: 1.5, height: 260, display: 'flex', flexDirection: 'column', borderRadius: 2, border: '1px solid #eee' }} elevation={0}>
            <Typography variant="body2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#444' }}>Dept-wise: Active Employees</Typography>
            <Box sx={{ flexGrow: 1, minHeight: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={stats.dept_active} dataKey="count" nameKey="department" cx="50%" cy="50%" outerRadius={70} label={{ fontSize: 10 }}>
                    {stats.dept_active?.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
        
        <Box sx={{ width: { xs: '100%', md: '33.33%' }, p: 1 }}>
          <Paper sx={{ p: 1.5, height: 260, display: 'flex', flexDirection: 'column', borderRadius: 2, border: '1px solid #eee' }} elevation={0}>
            <Typography variant="body2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#444' }}>Dept-wise: Resigned</Typography>
            <Box sx={{ flexGrow: 1, minHeight: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={stats.dept_resigned} dataKey="count" nameKey="department" cx="50%" cy="50%" outerRadius={70} label={{ fontSize: 10 }}>
                    {stats.dept_resigned?.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ width: { xs: '100%', md: '33.33%' }, p: 1 }}>
          <Paper sx={{ p: 1.5, height: 260, display: 'flex', flexDirection: 'column', borderRadius: 2, border: '1px solid #eee' }} elevation={0}>
            <Typography variant="body2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#444' }}>Dept-wise: Inactive</Typography>
            <Box sx={{ flexGrow: 1, minHeight: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={stats.dept_inactive} dataKey="count" nameKey="department" cx="50%" cy="50%" outerRadius={70} label={{ fontSize: 10 }}>
                    {stats.dept_inactive?.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default EmployeeDashboard;